import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, Loader2, Gift, Star, Crown, Gem } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await register({
        name,
        email,
        phone: phone || null,
        password,
        role: 'CUSTOMER',
        language: 'es'
      });
      toast.success('¡Bienvenido a CG Valet Club! Tu cuenta ha sido creada.');
      navigate('/account');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: '🥉', tier: 'Bronce', discount: '5%', color: '#cd7f32' },
    { icon: '🥈', tier: 'Plata', discount: '10%', color: '#c0c0c0' },
    { icon: '🥇', tier: 'Oro', discount: '15%', color: '#FFD700' },
    { icon: '💎', tier: 'Platino', discount: '20%', color: '#e5e4e2' },
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="logo-link">
          <img src="/brand/cg-valet-parking.png" alt="CG Valet Parking" className="h-24 w-auto object-contain" />
        </Link>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Benefits Card */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="card-dark p-8 hidden md:block"
          >
            <div className="text-center mb-6">
              <Gift className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-primary mb-2">
                CG Valet Club
              </h2>
              <p className="text-zinc-400">
                Únete gratis y disfruta de beneficios exclusivos
              </p>
            </div>

            <div className="space-y-4 mb-6">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={benefit.tier}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + idx * 0.1 }}
                  className="flex items-center gap-4 p-3 rounded-lg bg-white/5"
                >
                  <span className="text-2xl">{benefit.icon}</span>
                  <div className="flex-1">
                    <p className="text-white font-medium" style={{ color: benefit.color }}>
                      {benefit.tier}
                    </p>
                    <p className="text-sm text-zinc-400">
                      {benefit.discount} descuento en reservas
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-6 space-y-3">
              <div className="flex items-center gap-3 text-zinc-300">
                <Star className="w-5 h-5 text-primary" />
                <span className="text-sm">Acumula puntos por cada reserva</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300">
                <Crown className="w-5 h-5 text-primary" />
                <span className="text-sm">Sube de nivel automáticamente</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300">
                <Gem className="w-5 h-5 text-primary" />
                <span className="text-sm">Ofertas y promociones exclusivas</span>
              </div>
            </div>
          </motion.div>

          {/* Register Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="card-dark p-8"
          >
            {/* Mobile benefits summary */}
            <div className="md:hidden text-center mb-6 pb-6 border-b border-white/10">
              <Gift className="w-10 h-10 text-primary mx-auto mb-2" />
              <h2 className="font-heading text-xl font-bold text-primary">
                Únete a CG Valet Club
              </h2>
              <p className="text-sm text-zinc-400 mt-1">
                Hasta 20% de descuento en tus reservas
              </p>
            </div>

            <h1 className="font-heading text-2xl font-bold text-white text-center mb-6">
              Crear cuenta gratis
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white">{t('auth.name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre completo"
                    className="input-gold pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="input-gold pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white">{t('auth.phone')} <span className="text-zinc-500">(opcional)</span></Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+34 600 000 000"
                    className="input-gold pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="input-gold pl-10"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white">{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite tu contraseña"
                    className="input-gold pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary text-base py-6"
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando cuenta...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Unirme a CG Valet Club
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-zinc-400 text-sm">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                  Iniciar sesión
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
